<script setup lang="ts">
import type { CarouselItemSelectorProps } from '.';
import type { HTMLAttributes } from 'vue';

import { cn } from '@/utils';

import {
	carouselItemSelectorBarStyles,
	carouselItemSelectorContainerStyles
} from '.';

const props = withDefaults(defineProps<CarouselItemSelectorProps>(), {
	variant: 'white',
	active: false,
	delay: 1000
});

enum SelectorOperations {
	FILLING = 'filling',
	DRYING = 'drying'
}

const operationName = ref<SelectorOperations>(SelectorOperations.FILLING);
const whiteBarRef = ref<HTMLElement | undefined>(undefined);

let barAnimation: Animation | null = null;

function animateBar(
	from: number,
	to: number,
	origin: 'left' | 'right',
	duration: number
) {
	const bar = whiteBarRef.value;
	// happy-dom (testes) nao implementa Element.animate
	if (!bar || typeof bar.animate !== 'function') return;
	barAnimation?.cancel();
	bar.style.transformOrigin = origin;
	barAnimation = bar.animate(
		[{ transform: `scaleX(${from})` }, { transform: `scaleX(${to})` }],
		{ duration, easing: 'linear', fill: 'forwards' }
	);
}

function stopAnimation() {
	barAnimation?.cancel();
	barAnimation = null;
}

async function startAnimation() {
	operationName.value = SelectorOperations.FILLING;
	animateBar(0, 1, 'left', props.delay);
}

function endAnimation() {
	operationName.value = SelectorOperations.DRYING;
	animateBar(1, 0, 'right', props.delay / 3);
}

const isFilling = computed(
	() => operationName.value === SelectorOperations.FILLING
);

watch(
	() => props.active,
	isActive => {
		if (isActive) {
			startAnimation();
		} else {
			endAnimation();
		}
	},
	{ immediate: true }
);

const isDynamicWidth = computed(
	() => props.dynamicWidth && !isNaN(props.dynamicWidth)
);

const style = computed(() => ({
	...(isDynamicWidth.value
		? {
				width: `${props.dynamicWidth}px`,
				maxWidth: `60px`,
				minWidth: `5px`,
				flexGrow: 1,
				flexShrink: 0
			}
		: {})
}));

onBeforeUnmount(stopAnimation);

defineExpose({ stopAnimation });
</script>

<template>
	<button
		:class="[
			cn(
				carouselItemSelectorContainerStyles({
					variant,
					dynamicWidth: Boolean(isDynamicWidth),
					size
				}),
				props.class
			),
			isFilling ? 'justify-start' : 'justify-end'
		]"
		:style="style"
	>
		<div
			ref="whiteBarRef"
			:class="carouselItemSelectorBarStyles({ variant })"
			:style="{ transform: active ? 'scaleX(1)' : 'scaleX(0)' }"
		></div>
	</button>
</template>
